/**
 * PageCanvas.jsx
 * 
 * Central canvas showing the selected page at full size.
 * Renders different layouts and allows inline editing.
 * 
 * Features:
 * - Real-time layout preview
 * - Inline text editing
 * - Photo management (add/remove/reorder)
 * - Aspect ratio matching print dimensions
 */

import React, { useMemo, useEffect } from 'react';
import { useEditor } from './EditorContext';
import {
  Image as ImageIcon,
  Plus,
  X,
  Edit3,
  Type,
  Trash2,
  Copy,
  ArrowUpDown
} from 'lucide-react';

// Book dimensions (square format)
const ASPECT_RATIO = 1; // 1:1 for square book

export default function PageCanvas({ page, entryCache, onFetchEntries }) {
  const { 
    removeEntryFromPage, 
    updatePageText, 
    toggleEntryPicker,
    deletePage,
    duplicatePage
  } = useEditor();
  
  // Fetch entry data when page changes
  useEffect(() => {
    if (page?.entries?.length > 0) {
      const missingEntries = page.entries.filter(id => !entryCache[id]);
      if (missingEntries.length > 0 && onFetchEntries) {
        onFetchEntries(missingEntries);
      }
    }
  }, [page?.entries, entryCache, onFetchEntries]);
  
  // Get entries for this page
  const entries = useMemo(() => {
    if (!page?.entries) return [];
    return page.entries
      .map(id => entryCache[id])
      .filter(Boolean);
  }, [page?.entries, entryCache]);
  
  if (!page) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-400">Select a page to edit</p>
      </div>
    );
  }
  
  return (
    <div className="flex flex-col items-center">
      {/* Page actions */}
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => toggleEntryPicker(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-ocean-700 bg-ocean-50 hover:bg-ocean-100 rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Content
        </button>
        
        {!page.isLocked && (
          <>
            <button
              onClick={() => duplicatePage(page.id)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <Copy className="w-4 h-4" />
              Duplicate
            </button>
            
            <button
              onClick={() => deletePage(page.id)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          </>
        )}
      </div>
      
      {/* Page canvas */}
      <div 
        className="relative bg-white rounded-lg shadow-xl overflow-hidden"
        style={{
          width: '100%',
          maxWidth: 500,
          aspectRatio: ASPECT_RATIO,
        }}
      >
        {/* Render based on layout type */}
        <PageLayoutRenderer 
          page={page} 
          entries={entries}
          onRemoveEntry={(entryId) => removeEntryFromPage(page.id, entryId)}
          onUpdateText={(text) => updatePageText(page.id, text)}
        />
        
        {/* Page number overlay */}
        <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/50 text-white text-xs rounded">
          Page {page.sortOrder + 1}
        </div>
      </div>
      
      {/* Layout info */}
      <p className="mt-3 text-sm text-gray-500">
        Layout: {getLayoutLabel(page.layout)} • {entries.length} items
      </p>
    </div>
  );
}

// ============================================
// LAYOUT RENDERER
// ============================================

function PageLayoutRenderer({ page, entries, onRemoveEntry, onUpdateText }) {
  const layout = page.layout;
  
  // Cover page
  if (page.type === 'cover') {
    return <CoverLayout page={page} entries={entries} />;
  }
  
  // Table of contents
  if (page.type === 'toc') {
    return <TocLayout page={page} />;
  }
  
  // Section divider
  if (page.type === 'section') {
    return <SectionLayout page={page} onUpdateText={onUpdateText} />;
  }
  
  // Content layouts
  switch (layout) {
    case 'hero':
      return <HeroLayout entries={entries} onRemoveEntry={onRemoveEntry} />;
    case 'hero-text':
      return <HeroTextLayout page={page} entries={entries} onRemoveEntry={onRemoveEntry} onUpdateText={onUpdateText} />;
    case 'grid-2':
      return <GridLayout entries={entries} columns={2} onRemoveEntry={onRemoveEntry} />;
    case 'grid-3':
      return <Grid3Layout entries={entries} onRemoveEntry={onRemoveEntry} />;
    case 'grid-4':
      return <GridLayout entries={entries} columns={2} rows={2} onRemoveEntry={onRemoveEntry} />;
    case 'text-left':
      return <TextPhotoLayout page={page} entries={entries} textSide="left" onRemoveEntry={onRemoveEntry} onUpdateText={onUpdateText} />;
    case 'text-right':
      return <TextPhotoLayout page={page} entries={entries} textSide="right" onRemoveEntry={onRemoveEntry} onUpdateText={onUpdateText} />;
    case 'text-only':
      return <TextOnlyLayout page={page} entries={entries} onUpdateText={onUpdateText} />;
    case 'full-bleed':
      return <FullBleedLayout entries={entries} onRemoveEntry={onRemoveEntry} />;
    default:
      return <HeroLayout entries={entries} onRemoveEntry={onRemoveEntry} />;
  }
}

// ============================================
// LAYOUT COMPONENTS
// ============================================

function CoverLayout({ page, entries }) {
  const coverPhoto = entries[0]?.thumbnailUrl;
  
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center p-8 bg-gradient-to-b from-ocean-50 to-ocean-100">
      {coverPhoto && (
        <div className="w-32 h-32 rounded-full overflow-hidden shadow-lg mb-6">
          <img src={coverPhoto} alt="Cover" className="w-full h-full object-cover" />
        </div>
      )}
      <h1 className="text-2xl md:text-3xl font-serif text-ocean-900 text-center">
        {page.sectionTitle || 'Baby Book'}
      </h1>
      <p className="mt-2 text-sm text-ocean-700">A BabyBookin Memory Book</p>
    </div>
  );
}

function TocLayout({ page }) {
  return (
    <div className="absolute inset-0 flex flex-col p-8 bg-white">
      <h2 className="text-xl font-serif text-ocean-900 mb-6">Table of Contents</h2>
      <p className="text-gray-400 text-sm italic">
        Auto-generated when printing
      </p>
    </div>
  );
}

function SectionLayout({ page, onUpdateText }) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center p-8 bg-gradient-to-br from-ocean-50 to-white">
      <h2 className="text-2xl md:text-3xl font-serif text-ocean-900 text-center">
        {page.sectionTitle || 'Section'}
      </h2>
      {page.customText && (
        <p className="mt-4 text-ocean-700 text-center max-w-xs">
          {page.customText}
        </p>
      )}
    </div>
  );
}

function HeroLayout({ entries, onRemoveEntry }) {
  const entry = entries[0];
  
  if (!entry) {
    return <EmptySlot label="Add a photo" />;
  }
  
  return (
    <div className="absolute inset-0">
      <img 
        src={entry.thumbnailUrl} 
        alt="" 
        className="w-full h-full object-cover"
      />
      <EntryOverlay entry={entry} onRemove={() => onRemoveEntry(entry.id)} />
    </div>
  );
}

function HeroTextLayout({ page, entries, onRemoveEntry, onUpdateText }) {
  const entry = entries[0];
  
  return (
    <div className="absolute inset-0 flex flex-col">
      <div className="flex-1 relative">
        {entry ? (
          <>
            <img src={entry.thumbnailUrl} alt="" className="w-full h-full object-cover" />
            <EntryOverlay entry={entry} onRemove={() => onRemoveEntry(entry.id)} />
          </>
        ) : (
          <EmptySlot label="Add a photo" />
        )}
      </div>
      <div className="p-4 bg-white">
        <TextArea 
          value={page.customText || entry?.text || ''} 
          onChange={onUpdateText}
          placeholder="Add a caption..."
        />
      </div>
    </div>
  );
}

function GridLayout({ entries, columns = 2, rows = 1, onRemoveEntry }) {
  const totalSlots = columns * rows;
  const slots = Array(totalSlots).fill(null).map((_, i) => entries[i] || null);
  
  return (
    <div 
      className="absolute inset-0 grid gap-1 p-1"
      style={{ 
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gridTemplateRows: `repeat(${rows}, 1fr)`
      }}
    >
      {slots.map((entry, i) => (
        <div key={i} className="relative overflow-hidden rounded">
          {entry ? (
            <>
              <img src={entry.thumbnailUrl} alt="" className="w-full h-full object-cover" />
              <EntryOverlay entry={entry} onRemove={() => onRemoveEntry(entry.id)} mini />
            </>
          ) : (
            <EmptySlot mini />
          )}
        </div>
      ))}
    </div>
  );
}

function Grid3Layout({ entries, onRemoveEntry }) {
  return (
    <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 gap-1 p-1">
      {/* Large photo on left */}
      <div className="row-span-2 relative overflow-hidden rounded">
        {entries[0] ? (
          <>
            <img src={entries[0].thumbnailUrl} alt="" className="w-full h-full object-cover" />
            <EntryOverlay entry={entries[0]} onRemove={() => onRemoveEntry(entries[0].id)} />
          </>
        ) : (
          <EmptySlot />
        )}
      </div>
      {/* Two smaller photos on right */}
      <div className="relative overflow-hidden rounded">
        {entries[1] ? (
          <>
            <img src={entries[1].thumbnailUrl} alt="" className="w-full h-full object-cover" />
            <EntryOverlay entry={entries[1]} onRemove={() => onRemoveEntry(entries[1].id)} mini />
          </>
        ) : (
          <EmptySlot mini />
        )}
      </div>
      <div className="relative overflow-hidden rounded">
        {entries[2] ? (
          <>
            <img src={entries[2].thumbnailUrl} alt="" className="w-full h-full object-cover" />
            <EntryOverlay entry={entries[2]} onRemove={() => onRemoveEntry(entries[2].id)} mini />
          </>
        ) : (
          <EmptySlot mini />
        )}
      </div>
    </div>
  );
}

function TextPhotoLayout({ page, entries, textSide, onRemoveEntry, onUpdateText }) {
  const entry = entries[0];
  
  const photoSection = (
    <div className="w-1/2 relative">
      {entry ? (
        <>
          <img src={entry.thumbnailUrl} alt="" className="w-full h-full object-cover" />
          <EntryOverlay entry={entry} onRemove={() => onRemoveEntry(entry.id)} />
        </>
      ) : (
        <EmptySlot />
      )}
    </div>
  );
  
  const textSection = (
    <div className="w-1/2 p-4 flex flex-col justify-center bg-white">
      <TextArea 
        value={page.customText || entry?.text || ''} 
        onChange={onUpdateText}
        placeholder="Add your story..."
        large
      />
    </div>
  );
  
  return (
    <div className="absolute inset-0 flex">
      {textSide === 'left' ? (
        <>{textSection}{photoSection}</>
      ) : (
        <>{photoSection}{textSection}</>
      )}
    </div>
  );
}

function TextOnlyLayout({ page, entries, onUpdateText }) {
  const combinedText = page.customText || entries.map(e => e?.text).filter(Boolean).join('\n\n');
  
  return (
    <div className="absolute inset-0 p-6 flex flex-col bg-white">
      {page.sectionTitle && (
        <h3 className="text-lg font-serif text-ocean-900 mb-4">
          {page.sectionTitle}
        </h3>
      )}
      <TextArea 
        value={combinedText} 
        onChange={onUpdateText}
        placeholder="Add text..."
        large
      />
    </div>
  );
}

function FullBleedLayout({ entries, onRemoveEntry }) {
  const entry = entries[0];
  
  return (
    <div className="absolute inset-0">
      {entry ? (
        <>
          <img src={entry.thumbnailUrl} alt="" className="w-full h-full object-cover" />
          <EntryOverlay entry={entry} onRemove={() => onRemoveEntry(entry.id)} />
        </>
      ) : (
        <EmptySlot label="Add a full-page photo" />
      )}
    </div>
  );
}

// ============================================
// HELPER COMPONENTS
// ============================================

function EmptySlot({ label = 'Add content', mini = false }) {
  return (
    <div className={`
      w-full h-full bg-gray-100 flex flex-col items-center justify-center
      border-2 border-dashed border-gray-300 rounded
      ${mini ? 'text-xs' : 'text-sm'}
    `}>
      <ImageIcon className={`${mini ? 'w-4 h-4' : 'w-8 h-8'} text-gray-300 mb-1`} />
      <span className="text-gray-400">{label}</span>
    </div>
  );
}

function EntryOverlay({ entry, onRemove, mini = false }) {
  return (
    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 hover:opacity-100 transition-opacity">
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className={`
          absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full
          hover:bg-red-600 transition-colors
          ${mini ? 'w-5 h-5' : 'w-6 h-6'}
        `}
      >
        <X className="w-full h-full" />
      </button>
      
      {entry.timestamp && !mini && (
        <div className="absolute bottom-2 left-2 text-white text-xs">
          {new Date(entry.timestamp).toLocaleDateString()}
        </div>
      )}
    </div>
  );
}

function TextArea({ value, onChange, placeholder, large = false }) {
  return (
    <textarea
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`
        w-full resize-none border-0 focus:ring-0 bg-transparent
        placeholder-gray-300 text-gray-700
        ${large ? 'text-sm h-full' : 'text-xs h-16'}
      `}
    />
  );
}

function getLayoutLabel(layout) {
  const labels = {
    'hero': '1 Photo',
    'hero-text': '1 Photo + Caption',
    'grid-2': '2 Photos',
    'grid-3': '3 Photos',
    'grid-4': '4 Photos',
    'text-left': 'Text Left',
    'text-right': 'Text Right',
    'text-only': 'Text Only',
    'full-bleed': 'Full Bleed',
    'collage': 'Collage',
  };
  return labels[layout] || layout;
}
